import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import MappingChoices from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/components/MappingChoices';
import {
    MappingOptionType,
    OrgChartHrMapping,
} from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/helpers/type';
import { usePostOrgChartHrMapping } from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/helpers/hooks/orgChartHrMappings';
import { GridApi } from 'ag-grid-community';
import { StyMappingHeader } from 'routes/HumanResourcesModule/Settings/helpers/styles';

type Props = {
    selectedOrgChartHRSoftwareIds: string[];
    gridApi: GridApi;
};

export default function HRMappingsHeader({
    selectedOrgChartHRSoftwareIds,
    gridApi,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const updateMappingMutation = usePostOrgChartHrMapping();

    const handleChange = useCallback(
        (
            _event: React.SyntheticEvent,
            newValue: OrgChartHrMapping | MappingOptionType | null,
        ) => {
            if (newValue === null) return;
            const inputValue =
                'inputValue' in newValue ? newValue.inputValue : undefined;
            const { title } = newValue;

            // Set `finalValue` to the value of `inputValue` if it exists, otherwise set it to the value of `title`.
            // An inoutValue is set only in the case when a user created a new value that didn't exist previously
            const finalInput = inputValue ?? title;

            if (finalInput) {
                // If there is an input from user, we save it.
                updateMappingMutation.mutate({
                    title: finalInput,
                    orgChartHrSoftwareIds: selectedOrgChartHRSoftwareIds,
                });
                gridApi.deselectAll();
            }
        },
        [gridApi, selectedOrgChartHRSoftwareIds, updateMappingMutation],
    );

    return (
        <StyMappingHeader>
            <Box>
                <Typography variant="body1">
                    {t('humanResources.settings.thereIsNOrgChartHr', {
                        count: selectedOrgChartHRSoftwareIds.length,
                    })}
                </Typography>

                {selectedOrgChartHRSoftwareIds.length > 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '2em',
                        }}
                    >
                        <Typography variant="body1">
                            {t('humanResources.settings.chooseAnEquivalent')}
                        </Typography>
                        <MappingChoices handleChange={handleChange} />
                    </Box>
                ) : (
                    <Typography variant="body1">
                        {t('humanResources.settings.chooseMultiple')}
                    </Typography>
                )}
            </Box>
        </StyMappingHeader>
    );
}
